/**
 * @author Tyrus Malmstrom.
 *
 * Videojs plugin that adds hotkeys to our videojs player instance.
 */

/*
  eslint class-methods-use-this: ["warn", {
    "exceptMethods": [
      "pluginShouldBeInitialized",
      "fullscreenKey",
      "muteKey",
      "volumeUpKey",
      "volumeDownKey",
      "sidezoneKey"
    ]
  }]
*/

import videojs from 'video.js';
import semver from 'semver';

import ConfettiGenerator from 'confetti-js';

const [Plugin, registerPlugin, mergeOptions] = [
  videojs.getPlugin('plugin'),
  videojs.registerPlugin || videojs.plugin,
  videojs.mergeOptions || videojs.util.mergeOptions,
];

class VideojsHotkeys extends Plugin {
  #interactionCurrentlyVisible = false;

  #playerDOMElement;

  /**
   * Passed in plugin options.
   */
  #options;

  /**
   * the finalized plugin options object.
   */
  #finalPluginOptions;

  /**
   * Our Player play-toggle overlay container that resides on top of the videojs instance.
   */
  #overlayContainer = document.getElementsByClassName('overlay-play-toggle')[0];

  /**
   * Mutation observer to watch for our player sidebar opening and closing.
   */
  #observer = null;

  #defaultPluginOptions = {
    captureDocumentHotkeys: false,
    enableFullscreen: true,
    enableMute: true,
    enableVolumeChange: true,
    enableToggleSidezone: true,
    enableSettingsMenu: true,
    enablePlayerSeekNavigation: true,
    volumeOffset: 0.1,
    fullscreenKey: this.fullscreenKey,
    muteKey: this.muteKey,
    volumeUpKey: this.volumeUpKey,
    volumeDownKey: this.volumeDownKey,
    sidezoneKey: this.sidezoneKey,
    settingsMenuKey: this.settingsMenuKey,
  };

  /**
   * Default defined hotkey values.
   */
  #defaultHotkeys = {
    fullscreen: 1,
    mute: 2,
    volumeUp: 3,
    volumeDown: 4,
    sidezone: 5,
    settingsMenu: 6,
  };

  /**
   * Event handler functions.
   */
  #playerDoubleClickEventHandler;
  #playerKeyDownEventHandler;
  #konamiCodeEventHandler;

  #konamiCode = [
    'ArrowUp',
    'ArrowUp',
    'ArrowDown',
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight',
    'ArrowLeft',
    'ArrowRight',
    'b',
    'a',
  ];

  #konamiCurrentInput = 0;

  /**
   * Creates an instance of this class.
   *
   * @param {Player} player A Video.js player instance.
   * @param {object} options options object passed to the plugin instance.
   */
  constructor(player, options) {
    super(player, options);
    if (semver.lt(videojs.VERSION, '7.18.1')) {
      throw new Error(
        `This plugin requires the videojs version 7.18.1. Please upgrade from your current version: ${videojs.VERSION}`,
      );
    }
    const { videoInstanceVm } = options;
    if (!this.#pluginShouldBeInitialized({ videoInstanceVm })) return;
    this.player.on('beforepluginsetup', () => {
      this.#konamiCodeEventHandler = this.#listenForKonamiCode.bind(this);
      document.addEventListener('keydown', this.#konamiCodeEventHandler);
      this.initializeHotkeys({ options });
    });
  }

  /**
   * Method that determines whether or not the plugin should be initialized.
   *
   * @param {object} options passed in plugins options
   *
   * @returns {boolean} whether or not the plugin should be initialized
   */
  #pluginShouldBeInitialized({ videoInstanceVm } = {}) { // eslint-disable-line
    if (videoInstanceVm.mobile) return false;
    if (videoInstanceVm.isLiveSession) return false;
    if (videoInstanceVm.isBroadcastStudent) return false;
    return true;
  }

  /**
   * Method that handles the visible interaction state within the player to disable or enable the hotkeys.
   *
   * @param {Object} customEventData
   */
  #handleVisbileInteractionState(customEventData) { // eslint-disable-line
    if (
      customEventData.detail.newValue
      || (
        customEventData.detail.activeInteraction
        || customEventData.detail.overlay
      )
    ) { // means the interaction is now visible, sidebar or hotspot
      this.#interactionCurrentlyVisible = true;
      document.removeEventListener('dblclick', this.#playerDoubleClickEventHandler);
      document.removeEventListener('keydown', this.#playerKeyDownEventHandler);
    } else { // interaction is not visible anymore
      this.#interactionCurrentlyVisible = false;
      document.addEventListener('keydown', this.#playerKeyDownEventHandler);
      document.addEventListener('dblclick', this.#playerDoubleClickEventHandler);
    }
  }

  /**
   * Method that creates a mutation observer to watch when our interaction-wrapper is
   * being displayed in our player.
   */
  #createPlayerMutationObserver() { // eslint-disable-line
    const [
      sideBarPanelTargetNode,
    ] = [
      document.getElementById('sidebar-panel'),
    ];

    const config = { attributes: true, childList: true, subtree: true };

    const callback = (mutationsList, observer) => { // eslint-disable-line
      for (const mutation of mutationsList) { // eslint-disable-line
        if (this.#interactionCurrentlyVisible) return;
        if (
          this.#options.videoInstanceVm.sidebarOpen
          && (
            ['rubric-tab', 'notes-tab', 'transcript-tab'].includes(this.#options.videoInstanceVm.sidebarTab)
          )
        ) {
          document.removeEventListener('dblclick', this.#playerDoubleClickEventHandler);
          document.removeEventListener('keydown', this.#playerKeyDownEventHandler);
        }

        if (this.#options.videoInstanceVm.sidebarOpen && this.#options.videoInstanceVm.sidebarTab === 'bulb-info-tab') {
          document.addEventListener('keydown', this.#playerKeyDownEventHandler);
          document.addEventListener('dblclick', this.#playerDoubleClickEventHandler);
        }
      }
    };

    const bindedCallback = callback.bind(this);
    this.#observer = new MutationObserver(bindedCallback);
    this.#observer.observe(sideBarPanelTargetNode, config);
  }

  /**
   * Initialize the hotkeys plugin, and configure any necessary events.
   *
   * @param {object} passed in plugin options
   */
  initializeHotkeys({ options } = {}) {
    this.#playerDOMElement = this.player.el();
    this.#options = options;

    // Set default player tabindex to handle keydown and doubleclick events
    if (!this.#playerDOMElement.hasAttribute('tabIndex')) this.#playerDOMElement.setAttribute('tabIndex', '-1');
    // Remove player outline to fix video performance issue
    this.#playerDOMElement.style.outline = 'none';

    this.#finalPluginOptions = mergeOptions(this.#defaultPluginOptions, options || {});

    this.#playerDoubleClickEventHandler = this.handlePlayerDoubleClick.bind(this);
    this.#playerKeyDownEventHandler = this.handlePlayerKeydown.bind(this);

    if (this.#finalPluginOptions.captureDocumentHotkeys) {
      document.addEventListener('dblclick', this.#playerDoubleClickEventHandler);
      document.addEventListener('keydown', this.#playerKeyDownEventHandler);
    } else {
      this.#overlayContainer.addEventListener('dblclick', this.#playerDoubleClickEventHandler);
      this.#overlayContainer.addEventListener('keydown', this.#playerKeyDownEventHandler);
    }
    this.#createPlayerMutationObserver();
    document.addEventListener('hotkeys:visible-interaction', this.#handleVisbileInteractionState.bind(this));
  }

  fullscreenKey(keyboardEvent) {
    return keyboardEvent.key === 'f' || keyboardEvent.which === 70;
  }

  muteKey(keyboardEvent) {
    return keyboardEvent.key === 'm' || keyboardEvent.which === 77;
  }

  volumeUpKey(keyboardEvent) {
    return keyboardEvent.key === 'ArrowUp' || keyboardEvent.which === 38;
  }

  volumeDownKey(keyboardEvent) {
    return keyboardEvent.key === 'ArrowDown' || keyboardEvent.which === 40;
  }

  sidezoneKey(keyboardEvent) {
    return keyboardEvent.key === '/' || keyboardEvent.which === 191;
  }

  settingsMenuKey(keyboardEvent) { // eslint-disable-line
    return keyboardEvent.key === 's' || keyboardEvent.which === 83;
  }

  /**
   * Method that handles and delegates keyboard shortcuts in our player.
   *
   * @param {KeyboardEvent} event
   */
  handlePlayerKeydown(event) {
    const keyPressedInfo = {
      event,
      key: event.key,
      which: event.which,
      preventDefaultForPassedInEvent: event.preventDefault.bind(event),
    };

    switch (this.#verifyKey(event)) {
      case this.#defaultHotkeys.fullscreen:
        if (this.#finalPluginOptions.enableFullscreen) this.#enableFullscreen();
        break;
      case this.#defaultHotkeys.mute:
        if (this.#finalPluginOptions.enableMute) this.#mutePlayer();
        break;
      case this.#defaultHotkeys.volumeUp:
        if (this.#finalPluginOptions.enableVolumeChange) this.#increasePlayerVolume();
        break;
      case this.#defaultHotkeys.volumeDown:
        if (this.#finalPluginOptions.enableVolumeChange) this.#lowerPlayerVolume();
        break;
      case this.#defaultHotkeys.sidezone:
        if (this.#finalPluginOptions.enableToggleSidezone) this.#toggleSidezone();
        break;
      case this.#defaultHotkeys.settingsMenu:
        if (this.#finalPluginOptions.enableSettingsMenu) this.#toggleSettingsMenu();
        break;
      default:
        if (this.#finalPluginOptions.enablePlayerSeekNavigation) this.#handlePlayerSeekNavigation({ keyPressedInfo });
        break;
    }
  }

  /**
   * Function that handles when the user invokes a double-click on our player overlay target.
   *
   * @param {*} event KeyboardEvent
   */
  handlePlayerDoubleClick(event) {
    const currentActiveElement = event.relatedTarget || event.toElement || document.activeElement;
    if (currentActiveElement === this.#overlayContainer && this.#finalPluginOptions.enableFullscreen) this.#enableFullscreen();
  }

  /**
   * Method that handles certain player seek navigation keyboard shortcuts.
   *
   * @param {keyboardEvent} event
   *
   * @returns void
   */
  #handlePlayerSeekNavigation({ keyPressedInfo } = {}) { // eslint-disable-line
    const {
      event, key, which, preventDefaultForPassedInEvent,
    } = keyPressedInfo;
    if (['ArrowRight', 'ArrowLeft'].includes(key) || [37, 39].includes(which)) {
      let add = 0;
      if (event.ctrlKey || event.metaKey) {
        preventDefaultForPassedInEvent();
        add = key === 'ArrowLeft' ? -10 : 10;
      } else if (event.shiftKey) {
        add = key === 'ArrowLeft' ? -5 : 5;
      } else {
        add = key === 'ArrowLeft' ? -1 : 1;
      }
      this.player.trigger('hotkeys:seekNavigation', { arrowType: key, seekStep: Math.abs(add) });
      this.#options.EventBus.$emit('requestAction', {
        key: 'seek',
        seekTime: Math.min(
          this.#options.videoInstanceVm.displayTime + add,
          this.#options.videoInstanceVm.displayDuration,
        ),
      });
    }
  }

  /**
   * Verifies the key event from the player action initiated by the user.
   *
   * @param {KeyboardEvent} event Keyboard event
   *
   * @returns The configured player hotkey value
   */
  #verifyKey(event) { // eslint-disable-line
    if (this.#finalPluginOptions.fullscreenKey(event)) return this.#defaultHotkeys.fullscreen;
    if (this.#finalPluginOptions.muteKey(event)) return this.#defaultHotkeys.mute;
    if (this.#finalPluginOptions.volumeUpKey(event)) return this.#defaultHotkeys.volumeUp;
    if (this.#finalPluginOptions.volumeDownKey(event)) return this.#defaultHotkeys.volumeDown;
    if (this.#finalPluginOptions.sidezoneKey(event)) return this.#defaultHotkeys.sidezone;
    if (this.#finalPluginOptions.settingsMenuKey(event)) return this.#defaultHotkeys.settingsMenu;
    return 0;
  }

  /**
   * Helper function to enable fullscreen.
   *
   * Currently, we use version 3.3.2 of screenfull which is woefully outdated. In the most recent and newer versions
   * invoking the `toggle()` method actually returns a promise that resolves after the element enters/exits fullscreen.
   *
   * However in version 3.3.2 this isn't the case. So in theory we don't actually have to add the try/catch async logic here
   * however if we ever upgrade the version of screenfull we are using (which we should) this code will handle the newer logic
   * appropriately. In other words, it doesn't harm anything to have it here.
   */
  async #enableFullscreen() { // eslint-disable-line
    try {
      await this.#options.screenfull.toggle();
    } catch (error) {
      /* eslint no-console: "off" */
      console.error(`Error entering fullscreen mode. Reason: ${error} with player id: ${this.player.id}`);
    }
  }

  #mutePlayer() { // eslint-disable-line
    this.player.trigger('hotkeys:mute', { mute: !this.player.muted() });
  }

  #lowerPlayerVolume() { // eslint-disable-line
    const calculatedVolume = (this.player.volume() - this.#finalPluginOptions.volumeOffset) * 100;
    this.player.trigger('hotkeys:volumeDown', { volume: calculatedVolume });
  }

  #increasePlayerVolume() { // eslint-disable-line
    const calculatedVolume = (this.player.volume() + this.#finalPluginOptions.volumeOffset) * 100;
    this.player.trigger('hotkeys:volumeUp', { volume: calculatedVolume });
  }

  #toggleSidezone() { // eslint-disable-line
    this.player.trigger('hotkeys:toggle-sidezone');
  }

  #toggleSettingsMenu() { // eslint-disable-line
    this.player.trigger('hotkeys:toggle-settings-menu');
  }

  dispose() {
    super.dispose();

    if (this.#finalPluginOptions.captureDocumentHotkeys) {
      document.removeEventListener('dblclick', this.#playerDoubleClickEventHandler);
      document.removeEventListener('keydown', this.#playerKeyDownEventHandler);
    } else {
      this.#overlayContainer.removeEventListener('dblclick', this.#playerDoubleClickEventHandler);
      this.#overlayContainer.removeEventListener('keydown', this.#playerKeyDownEventHandler);
    }

    this.#observer.disconnect();
    document.removeEventListener('hotkeys:visible-interaction', this.#handleVisbileInteractionState);
    document.removeEventListener('keydown', this.#konamiCodeEventHandler);
    videojs.log('The hotkeys plugin has been disposed and all event listeners have been removed.');
  }

  #listenForKonamiCode(event) { // eslint-disable-line
    if (this.#konamiCode.indexOf(event.key) < 0 || event.key !== this.#konamiCode[this.#konamiCurrentInput]) {
      this.#konamiCurrentInput = 0;
      return;
    }
    this.#konamiCurrentInput += 1;
    if (this.#konamiCode.length !== this.#konamiCurrentInput) return;
    this.#konamiCurrentInput = 0;
    this.#launchConfetti();
  }

  #launchConfetti() { // eslint-disable-line
    const confettiSettings = {
      target: 'player-confetti-container',
      max: 450,
      props: [
        'circle',
        'square',
        'triangle',
        'line',
        {
          type: 'svg',
          src: 'https://cdn.jsdelivr.net/gh/Tyru5/Tyru5/assets/gifs/dachshund-svgrepo-com.svg',
          weight: 0.5,
          size: 25,
        },
      ],
      clock: 55,
      rotate: true,
      start_from_edge: true,
      respawn: false,
    };
    const confetti = new ConfettiGenerator(confettiSettings);
    confetti.render();
  }
} // end of plugin class declaration.

// Include the version number.
VideojsHotkeys.VERSION = '1.0.0';

// (╯°□°)╯︵ ┻━┻ ┻━┻︵ \(°□°)/ ︵ ┻━┻ \\

// register the plugin on the videojs instance:
registerPlugin('hotkeys', VideojsHotkeys);

export default VideojsHotkeys;
